import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from "@angular/core";
import { provideRouter, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { addAccessTokenHeader, addCompanyIdentifierHeader, handleErrorInterceptor, refreshOnUnauthoriseInterceptor } from "@core/api/interceptors.api";
import { VoltaAuthService } from "@core/auth/volta-auth.service";
import { AccessService } from "@core/client/access.service";
import { CompanyModulesService } from "@core/client/company-modules.service";
import { CompanyService } from "@core/client/company.service";
import { UserService } from "@core/client/user.service";
import { CommonFunctions } from "@shared/services/common-functions.service";
import { provideToastr } from "ngx-toastr";
import { firstValueFrom, of, tap } from "rxjs";
import { routes } from "./app.routes";

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideRouter(
            routes,
            withComponentInputBinding(),
            withRouterConfig({paramsInheritanceStrategy: 'always'})
        ),
        provideHttpClient(
            withInterceptors(
                [
                    addAccessTokenHeader,
                    addCompanyIdentifierHeader,
                    handleErrorInterceptor,
                    refreshOnUnauthoriseInterceptor,
                ]
            )
        ),
        provideToastr(),
        provideAppInitializer(() => {

            const userService = inject(UserService);
            const companyService = inject(CompanyService);
            const companyModulesService = inject(CompanyModulesService);
            const accessService = inject(AccessService);
            const authService = inject(VoltaAuthService);
            const companyId = CommonFunctions.getCurrentCompanyIdFromURL(window.location.pathname);

            const initSubscriptions = () => {
                userService.initSubscriptions();
                companyService.initSubscriptions();
                companyModulesService.initSubscriptions();
                accessService.initSubscriptions();
            }

            if(authService.isAuthorised()){

                console.log('[AppInitializer] Fetching User...');
                let initCalls:Array<() => Promise<any>> = [
                    () => firstValueFrom(userService.getSessionUser())
                ];

                if(companyId){
                console.log('[AppInitializer] And Company...');
                    initCalls.push(
                        () => firstValueFrom(companyService.getCompany(+companyId))
                    );
                }

                return Promise.allSettled(initCalls.map((fn) => fn()))
                .then(() => {
                    console.log('[AppInitializer] User and Company fetched');

                    return userService.getSessionUserAdditionalData()
                    .then(() => {

                        let userCompanyCalls:Array<() => Promise<any>> = [];

                        if(companyId){
                            console.log('[AppInitializer] Fetching Company Roles and Licenses')
                            userCompanyCalls.push(
                                () => accessService.getUserCompanyRoles(),
                                () => accessService.getUserCompanyLicense(),
                                () => companyModulesService.getModules()
                            )
                        }

                        return Promise.allSettled(userCompanyCalls.map((fn) => fn()))
                        .then(() => {
                            console.log('[AppInitializer] Subsequent User/Company Info fetched');
                            initSubscriptions();
                            console.log('[AppInitializer] Subscribed to User/Company updates');
                        })
                        
                    })

                })
                .catch(() => {})

            }
            else{
                return of(undefined)
                .pipe(
                    tap(() => {
                        initSubscriptions();
                    })
                );
            }
        })
    ]
};

